import React, { lazy, Suspense, useState, useMemo, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Stars, MapPin } from 'lucide-react';
import { BortleBadge } from '@/components/locations/BortleBadge';
import { CalendarView } from './CalendarView';
import { NearbyWorkshops } from '../workshops/NearbyWorkshops';
import { NearbyWorkshopsBanner } from '../workshops/NearbyWorkshopsBanner';
import { calculateMonthData, getVisibilityRating } from '@/utils/calendarData';
import type { DayData } from '@/utils/calendarData';

const LocationMap = lazy(() => import('./LocationMap').then(m => ({ default: m.LocationMap })));

interface DashboardLocation {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  notes?: string | null;
  created_at?: string;
  bortle_class?: number | null;
  display_name?: string;
}

interface MilkyWayPlannerViewProps {
  selectedLocation: DashboardLocation;
  onLocationSelect: (location: DashboardLocation | null) => void;
  onBackToSelection: () => void;
}

export const MilkyWayPlannerView: React.FC<MilkyWayPlannerViewProps> = ({
  selectedLocation,
  onLocationSelect,
  onBackToSelection
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [selectedDay, setSelectedDay] = useState<number>(-1);
  const [activeMonth, setActiveMonth] = useState(new Date().getMonth());
  const [activeYear, setActiveYear] = useState(new Date().getFullYear());

  // Get the month data to find the selected day's core positions
  const monthData = useMemo(
    () => calculateMonthData(selectedLocation, activeMonth, activeYear),
    [selectedLocation, activeMonth, activeYear],
  );

  const selectedDayData: DayData | undefined = useMemo(() => {
    if (selectedDay < 1) return undefined;
    return monthData.find((d) => d.date === selectedDay);
  }, [monthData, selectedDay]);

  const coreOverlay = useMemo(() => {
    if (!selectedDayData) return undefined;
    return {
      dateLabel: selectedDayData.fullDate,
      riseAzimuth: selectedDayData.riseAzimuth,
      setAzimuth: selectedDayData.setAzimuth,
      peakAzimuth: selectedDayData.peakAzimuth,
      maxElevation: selectedDayData.maxElevation,
      startAltitude: selectedDayData.startAltitude,
      endAltitude: selectedDayData.endAltitude,
      peakAltitude: selectedDayData.peakAltitude,
      corePeakTime: selectedDayData.corePeakTime,
      coreStart: selectedDayData.galacticCoreStart,
      coreEnd: selectedDayData.galacticCoreEnd,
      positions: selectedDayData.corePositions,
      visibilityRating: getVisibilityRating(selectedDayData),
      moonPhase: selectedDayData.moonPhase,
      darkCoreMinutes: selectedDayData.darkCoreMinutes,
      notVisibleReason: selectedDayData.notVisibleReason,
    };
  }, [selectedDayData]);

  const handleDaySelect = (dayDate: number) => {
    const newDay = dayDate < 1 ? -1 : dayDate;
    setSelectedDay(newDay);
    // Scroll to map when a date is selected
    if (newDay > 0 && mapRef.current) {
      // Use nearest to avoid scrolling past the map on mobile with fixed headers
      setTimeout(() => {
        mapRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
    }
  };

  const handleMonthChange = useCallback((month: number, year: number) => {
    setActiveMonth((prev) => {
      if (prev !== month) setSelectedDay(-1); // only clear if month actually changed
      return month;
    });
    setActiveYear(year);
  }, []);

  return (
    <div className="space-y-6">
      {/* Planner breadcrumb bar */}
      <div className="flex items-center justify-between py-3 border-b border-border">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBackToSelection}
            className="text-muted-foreground hover:text-foreground -ml-2"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            Locations
          </Button>
          <span className="text-border">/</span>
          <div className="flex items-center gap-2">
            <Stars className="w-4 h-4 text-gold" />
            <span className="font-semibold text-foreground">Milky Way Planner</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <MapPin className="w-3.5 h-3.5" />
          <span>{selectedLocation.display_name || selectedLocation.name}</span>
          <span className="text-muted-foreground/50 ml-1">
            {selectedLocation.latitude.toFixed(2)}°, {selectedLocation.longitude.toFixed(2)}°
          </span>
          {selectedLocation.bortle_class != null && (
            <BortleBadge bortle={selectedLocation.bortle_class} />
          )}
        </div>
      </div>

      <NearbyWorkshopsBanner
        latitude={selectedLocation.latitude}
        longitude={selectedLocation.longitude}
        locationName={selectedLocation.display_name || selectedLocation.name}
      />

      <div ref={mapRef}>
      <Suspense fallback={<div className="h-[400px] rounded-xl bg-muted animate-pulse" />}>
        <LocationMap
          latitude={selectedLocation.latitude}
          longitude={selectedLocation.longitude}
          locationName={selectedLocation.name}
          coreOverlay={coreOverlay}
        />
      </Suspense>
      </div>

      {selectedDay > 0 && selectedDayData && (
        <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-gold/10 border border-gold/20 text-sm">
          <span className="text-foreground">
            Showing galactic core path for <strong>{selectedDayData.fullDate}</strong>
            {selectedDayData.galacticCoreVisible
              ? ` · ${selectedDayData.galacticCoreStart} – ${selectedDayData.galacticCoreEnd}`
              : ' · Not visible'}
          </span>
          <Button variant="ghost" size="sm" onClick={() => setSelectedDay(-1)} className="text-xs h-7">
            Clear
          </Button>
        </div>
      )}

      <CalendarView
        selectedLocation={selectedLocation}
        onLocationUpdate={onLocationSelect}
        hideLocationCard
        selectedDay={selectedDay}
        onDaySelect={handleDaySelect}
        onMonthChange={handleMonthChange}
      />

      <NearbyWorkshops
        latitude={selectedLocation.latitude}
        longitude={selectedLocation.longitude}
        locationName={selectedLocation.display_name || selectedLocation.name}
      />
    </div>
  );
};
